import { useState } from "react";
import { MagnifyingGlass, Plus, X, Trash, CaretDown, CaretRight } from "@phosphor-icons/react";
import { T } from "../../themes";
import { DEFAULT_EXERCISES } from "../../exerciseLibrary";
import {
  CATEGORIES, MUSCLE_GROUPS, uid,
  Btn
} from "../../workoutHelpers";
import AppHeader from "../../components/AppHeader";

// Collapsible group row used inside LibraryScreen
function LibraryGroup({groupKey, exercises, collapsedGroups, onToggle, onDeleteExercise, isDefault}){
  const open = !collapsedGroups[groupKey];
  const [showInstr, setShowInstr] = useState(null); // exercise id or null
  return(
    <div style={{marginBottom:8}}>
      {/* Group header -- always visible, click to toggle */}
      <button
        onClick={()=>onToggle(groupKey)}
        style={{
          display:"flex", justifyContent:"space-between", alignItems:"center",
          width:"100%", background:T.tileBg, border:"none",
          borderRadius: open ? "12px 12px 0 0" : 12,
          padding:"11px 16px", cursor:"pointer", marginBottom: open ? 0 : 4
        }}
      >
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {open
            ? <CaretDown size={14} weight="bold" color="white"/>
            : <CaretRight size={14} weight="bold" color="white"/>
          }
          <span style={{fontSize:14,fontFamily:T.fontDisplay,fontWeight:800,color:"white"}}>
            {groupKey}
          </span>
        </div>
        <span style={{fontSize:10,fontFamily:T.fontSans,fontWeight:600,color:"rgba(255,255,255,0.7)",background:"rgba(255,255,255,0.2)",padding:"2px 8px",borderRadius:T.radiusSm}}>
          {exercises.length}
        </span>
      </button>

      {/* Exercise rows -- only rendered when open */}
      {open && (
        <div style={{borderRadius:"0 0 12px 12px",overflow:"hidden"}}>
          {exercises.map((ex,i)=>(
            <div key={ex.id} style={{
              background: T.card,
              borderBottom: i<exercises.length-1 ? `1px solid ${T.cardBorder}` : "none",
              padding:"12px 16px",
              display:"flex", justifyContent:"space-between", alignItems:"center"
            }}>
              <div style={{flex:1,minWidth:0}}>
                <p style={{margin:0,fontSize:14,fontFamily:T.fontSans,fontWeight:600,color:T.inkDark}}>{ex.name}</p>
                <div style={{display:"flex",gap:4,marginTop:4,flexWrap:"wrap"}}>
                  <span style={{background:T.surfaceLight,borderRadius:20,padding:"3px 8px",fontSize:10,fontWeight:600,color:T.inkMuted}}>{ex.category}</span>
                  {(ex.muscles||[]).map(m=><span key={m} style={{background:T.surfaceLight,borderRadius:20,padding:"3px 8px",fontSize:10,fontWeight:600,color:T.inkMuted}}>{m}</span>)}
                </div>
                {ex.notes && <p style={{margin:"4px 0 0",fontSize:10,fontFamily:T.fontSans,color:T.inkMuted,fontStyle:"italic"}}>{ex.notes}</p>}
                {showInstr===ex.id&&ex.instructions&&(
                  <div style={{background:T.surfaceLight,padding:12,marginTop:6,borderRadius:T.radiusSm}}>
                    <p style={{margin:0,fontSize:12,fontFamily:T.fontSans,color:T.inkMuted,lineHeight:"1.6"}}>{ex.instructions}</p>
                  </div>
                )}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                {ex.instructions&&(
                  <button onClick={()=>setShowInstr(showInstr===ex.id?null:ex.id)} style={{background:showInstr===ex.id?(T.primary||T.accent):T.surfaceLight,border:`1px solid ${showInstr===ex.id?(T.primary||T.accent):T.cardBorder}`,
                    padding:"10px 14px",cursor:"pointer",color:showInstr===ex.id?"white":T.inkMuted,fontSize:10,fontFamily:T.fontSans,fontWeight:700,borderRadius:8}}>
                    How to
                  </button>
                )}
                {!isDefault(ex.id) && (
                  <button onClick={()=>onDeleteExercise(ex)} style={{background:"none",border:"none",cursor:"pointer",color:T.inkMuted,width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",padding:0,margin:"-10px -8px -10px 0"}}>
                    <Trash size={16} weight="bold"/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LibraryScreen({library,onAddExercise,onDeleteExercise,persona,darkMode,setDarkMode,profile}){
  const [search,setSearch]=useState("");
  // Two independent filter dimensions: equipment category and muscle group
  const [catFilter,setCatFilter]=useState("All");
  const [muscleFilter,setMuscleFilter]=useState("All");
  // Track which group keys are collapsed -- empty = all open
  const [collapsedGroups,setCollapsedGroups]=useState({});
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({name:"",category:"Barbell",muscles:[],notes:""});
  const [deleteTarget,setDeleteTarget]=useState(null);
  // filterMode: "category" | "muscle" -- which pill row is shown
  const [filterMode,setFilterMode]=useState("category");

  function toggleGroup(key){
    setCollapsedGroups(prev=>({...prev,[key]:!prev[key]}));
  }
  function collapseAll(){
    const all={};
    Object.keys(grouped).forEach(k=>{ all[k]=true; });
    setCollapsedGroups(all);
  }
  function expandAll(){
    setCollapsedGroups({});
  }

  // Apply search + category filter + muscle filter
  const filtered=library.filter(ex=>{
    const matchSearch=ex.name.toLowerCase().includes(search.toLowerCase());
    const matchCat=catFilter==="All"||ex.category===catFilter;
    const matchMuscle=muscleFilter==="All"||(ex.muscles||[]).includes(muscleFilter);
    return matchSearch&&matchCat&&matchMuscle;
  });

  // Group by whichever dimension is NOT the active filter
  // When filtering by muscle, group by category (and vice versa) for useful context
  // When no filter active, group by category (default)
  const grouped={};
  if(muscleFilter!=="All"){
    // Grouping by category when muscle filter is active
    for(const ex of filtered){
      const g=ex.category||"Other";
      if(!grouped[g]) grouped[g]=[];
      grouped[g].push(ex);
    }
  } else {
    // Default: group by category
    for(const ex of filtered){
      const g=ex.category||"Other";
      if(!grouped[g]) grouped[g]=[];
      grouped[g].push(ex);
    }
  }

  function toggleFormMuscle(m){
    setForm(f=>({...f,muscles:f.muscles.includes(m)?f.muscles.filter(x=>x!==m):[...f.muscles,m]}));
  }
  function handleAdd(){
    if(!form.name.trim()) return;
    onAddExercise({id:uid(),name:form.name.trim(),category:form.category,muscles:form.muscles.length>0?form.muscles:["Other"],notes:form.notes.trim()});
    setForm({name:"",category:"Barbell",muscles:[],notes:""});
    setShowAdd(false);
  }
  function isDefault(id){ return !!DEFAULT_EXERCISES.find(d=>d.id===id); }

  const sortedGroups=Object.entries(grouped).sort((a,b)=>a[0]<b[0]?-1:1);
  const totalFiltered=filtered.length;

  return(
    <div style={{background:T.pageBg,minHeight:"100dvh"}}>
      <AppHeader
        title="Library"
        subtitle={`${library.length} exercises`}
        currentApp="lift"
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        username={profile?.username}
      />

      <div style={{padding:"0 16px 8px"}}>

        {/* Search */}
        <div style={{background:T.surfaceLight,borderRadius:12,border:`1px solid ${T.cardBorder}`,padding:"10px 14px",display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <MagnifyingGlass size={16} weight="bold" color={T.inkMuted}/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search exercises..." style={{flex:1,background:"transparent",border:"none",outline:"none",fontSize:13,fontFamily:T.fontSans,color:T.inkDark}}/>
          {search&&<button onClick={()=>setSearch("")} style={{background:"none",border:"none",cursor:"pointer",color:T.inkMuted,padding:0}}><X size={14} weight="bold"/></button>}
        </div>

        {/* Filter mode toggle */}
        <div style={{display:"flex",gap:0,marginBottom:10,background:T.surfaceLight,borderRadius:12,overflow:"hidden",border:`1px solid ${T.cardBorder}`}}>
          {[{id:"category",label:"By Equipment"},{id:"muscle",label:"By Muscle"}].map(m=>(
            <button key={m.id} onClick={()=>{setFilterMode(m.id);if(m.id==="category"){setMuscleFilter("All");}else{setCatFilter("All");}}}
              style={{flex:1,padding:"9px 0",background:filterMode===m.id?T.primary:"transparent",border:"none",
                color:filterMode===m.id?"white":T.inkMuted,fontSize:11,fontWeight:700,fontFamily:T.fontSans,cursor:"pointer"}}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Category pills */}
        {filterMode==="category" && (
          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
            {["All",...CATEGORIES].map(c=>(
              <button key={c} onClick={()=>setCatFilter(c)}
                style={{background:catFilter===c?T.primary:T.surfaceLight,color:catFilter===c?"white":T.inkMuted,
                  border:`1px solid ${catFilter===c?T.primary:T.cardBorder}`,borderRadius:20,padding:"6px 14px",fontSize:11,fontFamily:T.fontSans,
                  fontWeight:600,cursor:"pointer"}}>
                {c}
              </button>
            ))}
          </div>
        )}

        {/* Muscle pills */}
        {filterMode==="muscle" && (
          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
            {["All",...MUSCLE_GROUPS].map(m=>(
              <button key={m} onClick={()=>setMuscleFilter(m)}
                style={{background:muscleFilter===m?T.primary:T.surfaceLight,color:muscleFilter===m?"white":T.inkMuted,
                  border:`1px solid ${muscleFilter===m?T.primary:T.cardBorder}`,borderRadius:20,padding:"6px 14px",fontSize:11,fontFamily:T.fontSans,
                  fontWeight:600,cursor:"pointer"}}>
                {m}
              </button>
            ))}
          </div>
        )}

        {/* Results count + collapse/expand controls */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <span style={{fontSize:11,fontFamily:T.fontSans,color:T.inkMuted,fontWeight:600}}>
            {totalFiltered} exercise{totalFiltered!==1?"s":""}
            {catFilter!=="All"||muscleFilter!=="All" ? " (filtered)" : ""}
          </span>
          <div style={{display:"flex",gap:6}}>
            <button onClick={expandAll} style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,color:T.inkMuted,padding:"4px 10px",fontSize:10,fontFamily:T.fontSans,fontWeight:600,cursor:"pointer",borderRadius:8}}>
              Expand all
            </button>
            <button onClick={collapseAll} style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,color:T.inkMuted,padding:"4px 10px",fontSize:10,fontFamily:T.fontSans,fontWeight:600,cursor:"pointer",borderRadius:8}}>
              Collapse all
            </button>
          </div>
        </div>

        <Btn onClick={()=>setShowAdd(true)} style={{marginBottom:14}}><Plus size={15} weight="bold"/> Add custom exercise</Btn>

        {/* Collapsible grouped list */}
        {sortedGroups.length===0 && (
          <div style={{textAlign:"center",padding:"20px 0",color:T.inkMuted}}>
            <p style={{fontSize:13,fontFamily:T.fontDisplay,fontWeight:800}}>No exercises match</p>
          </div>
        )}
        {sortedGroups.map(([cat,exs])=>(
          <LibraryGroup
            key={cat}
            groupKey={cat}
            exercises={exs}
            collapsedGroups={collapsedGroups}
            onToggle={toggleGroup}
            onDeleteExercise={ex=>setDeleteTarget(ex)}
            isDefault={isDefault}
          />
        ))}

        {/* Add exercise modal */}
        {showAdd&&(
          <div style={{position:"fixed",inset:0,background:T.backdrop,display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:100}}>
            <div style={{background:T.card,borderRadius:"24px 24px 0 0",width:"100%",maxWidth:480,maxHeight:"85vh",overflowY:"auto"}}>
              <div style={{padding:"18px 20px 14px",borderBottom:`1px solid ${T.cardBorder}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontFamily:T.fontDisplay,fontSize:20,fontWeight:800,color:T.inkDark}}>New exercise</span>
                <button onClick={()=>setShowAdd(false)} style={{background:"none",border:"none",cursor:"pointer",color:T.inkMuted}}><X size={18} weight="bold"/></button>
              </div>
              <div style={{padding:"16px 20px 20px"}}>
                <div style={{marginBottom:12}}>
                  <label style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,fontWeight:700,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:"1px"}}>Name</label>
                  <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Cable Fly" style={{width:"100%",background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:12,color:T.inkDark,fontFamily:T.fontSans,fontSize:13,padding:"10px 14px",outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,fontWeight:700,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:"1px"}}>Category</label>
                  <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} style={{width:"100%",background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:12,color:T.inkDark,fontFamily:T.fontSans,fontSize:13,padding:"10px 14px"}}>
                    {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,fontWeight:700,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:"1px"}}>Muscles</label>
                  <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                    {MUSCLE_GROUPS.map(m=>(
                      <button key={m} onClick={()=>toggleFormMuscle(m)}
                        style={{background:form.muscles.includes(m)?T.primary:T.surfaceLight,color:form.muscles.includes(m)?"white":T.inkMuted,
                          border:`1px solid ${form.muscles.includes(m)?T.primary:T.cardBorder}`,borderRadius:20,padding:"6px 12px",fontSize:10,fontFamily:T.fontSans,fontWeight:600,cursor:"pointer"}}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{marginBottom:14}}>
                  <label style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,fontWeight:700,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:"1px"}}>Notes (optional)</label>
                  <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Cues, technique notes..." style={{width:"100%",background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:12,color:T.inkDark,fontFamily:T.fontSans,fontSize:13,padding:"10px 14px",outline:"none",boxSizing:"border-box",minHeight:60,resize:"vertical"}}/>
                </div>
                <Btn onClick={handleAdd} disabled={!form.name.trim()}>Save exercise</Btn>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm */}
        {deleteTarget&&(
          <div style={{position:"fixed",inset:0,background:T.backdrop,display:"flex",alignItems:"center",justifyContent:"center",zIndex:100}}>
            <div style={{background:T.card,borderRadius:18,padding:24,maxWidth:300,width:"90%"}}>
              <p style={{margin:"0 0 8px",fontFamily:T.fontDisplay,fontSize:20,fontWeight:800,color:T.inkDark}}>Delete exercise?</p>
              <p style={{margin:"0 0 4px",fontSize:13,fontFamily:T.fontSans,color:T.inkMuted}}>{deleteTarget.name}</p>
              <p style={{margin:"0 0 16px",fontSize:12,fontFamily:T.fontSans,color:T.inkMuted}}>Past session data is unchanged.</p>
              <Btn danger onClick={()=>{onDeleteExercise(deleteTarget.id);setDeleteTarget(null);}} style={{marginBottom:8}}>Yes, delete</Btn>
              <Btn outline onClick={()=>setDeleteTarget(null)}>Cancel</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
